/**
 * i18n configuration — the single source of truth for which locales the SPA
 * supports, which of them are written right-to-left, and how the choice is
 * persisted.
 *
 * The catalogs are authored with FRENCH as the source (`fr.ts` defines the key
 * shape), so `fr` stays the FALLBACK for an unresolvable key. The product is
 * English-first for new visitors: on a first visit (no stored choice) the
 * locale is auto-detected from the browser's Accept-Language list
 * (`navigator.languages`); a language we do not ship falls back to English
 * (`DEFAULT_LOCALE`). `ar` is right-to-left (drives `document.dir = 'rtl'`).
 */

/** Every locale the UI can switch to. Order is the order shown in pickers. */
export const SUPPORTED_LOCALES = ['fr', 'en', 'ar'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** English-first: the locale used when the browser asks for nothing we ship. */
export const DEFAULT_LOCALE: Locale = 'en';
/** French is the catalog source language → the per-key fallback. */
export const FALLBACK_LOCALE: Locale = 'fr';

/**
 * Locales written right-to-left. On a switch to any of these, the locale store
 * sets `document.documentElement.dir = 'rtl'` (otherwise `'ltr'`). Kept as a Set
 * so adding a future RTL locale (he, fa, ur…) is a one-line change.
 */
export const RTL_LANGUAGES: ReadonlySet<Locale> = new Set<Locale>(['ar']);

/** localStorage key holding the raw locale string (e.g. "en"). */
export const LOCALE_STORAGE_KEY = 'psn-locale';

/**
 * localStorage key recording HOW the stored locale was chosen: `user` (picked in
 * a language switcher / restored from the profile) or `auto` (Accept-Language
 * detection). Only an explicit `user` choice is pushed to the profile.
 */
export const LOCALE_SOURCE_KEY = 'psn-locale-source';

export type LocaleSource = 'user' | 'auto';

/** Display metadata for each locale — used by the Profile language selector. */
export const LOCALE_META: Record<Locale, { label: string; nativeName: string; dir: 'ltr' | 'rtl' }> =
  {
    fr: { label: 'Français', nativeName: 'Français', dir: 'ltr' },
    en: { label: 'English', nativeName: 'English', dir: 'ltr' },
    ar: { label: 'العربية', nativeName: 'العربية', dir: 'rtl' },
  };

/** Type-guard: coerce any persisted/legacy value into a known locale. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Pick the best supported locale from an Accept-Language style list
 * (`navigator.languages`, e.g. `['fr-CA', 'en-US']`): the first entry whose
 * primary subtag we ship wins; nothing matching → English (`DEFAULT_LOCALE`).
 */
export function detectLocale(languages: readonly string[] | undefined | null): Locale {
  for (const tag of languages ?? []) {
    const primary = String(tag).trim().toLowerCase().split(/[-_]/)[0];
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/** The browser's Accept-Language preference list (SSR / test safe). */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

/** Reading direction for a locale. */
export function dirFor(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.has(locale) ? 'rtl' : 'ltr';
}
